import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { db } from "../firebase";
import { getDoc, setDoc, doc } from "firebase/firestore";
import { AvatarCreator, AvatarCreatorConfig, AvatarExportedEvent } from "@readyplayerme/react-avatar-creator";
import html2canvas from "html2canvas";
import "../styles/AvatarCreatorPage.css";

// Logo dosyasını import et (dosya yolunu projenize göre güncelleyin)
import logo from "../assets/logo.png";

const config = {
  clearCache: true,
  bodyType: "fullbody",
  quickStart: false,
  language: "tr",
};

const iframeStyle = { width: "100%", height: "600px", border: "none" };

const AvatarCreatorPage = () => {
  const userId = localStorage.getItem("userId");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);

  useEffect(() => {
    const loadSavedAvatar = async () => {
      if (!userId) {
        console.log("userId bulunamadı. localStorage kontrol ediliyor:", localStorage);
        return;
      }
      try {
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          console.log("Firebase'den yüklenen veri:", data);
          if (data.avatarUrl) {
            setAvatarUrl(data.avatarUrl);
            setShareUrl(data.shareUrl || null);
          } else {
            console.log("Kullanıcı verisinde avatarUrl bulunamadı.");
          }
        } else {
          console.log("Kullanıcı verisi bulunamadı, userId:", userId);
        }
      } catch (error) {
        console.error("Avatar verileri alınamadı:", error.message);
        toast.error("Avatar verileri yüklenemedi: " + error.message);
      }
    };
    loadSavedAvatar();
  }, [userId]);

  const handleSaveAvatar = async () => {
    if (!userId) {
      console.log("userId eksik, localStorage:", localStorage);
      toast.error("Oturum açmanız gerekiyor! userId bulunamadı.");
      return false;
    }
    if (!avatarUrl || !shareUrl) {
      console.log("avatarUrl veya shareUrl eksik:", { avatarUrl, shareUrl });
      toast.error("Avatar kaydedilemedi: Avatar URL'si eksik!");
      return false;
    }
    try {
      const userRef = doc(db, "users", userId);
      console.log("Firebase'e kaydediliyor:", { avatarUrl, shareUrl });
      await setDoc(userRef, { avatarUrl, shareUrl }, { merge: true });
      console.log("Firebase'e başarıyla kaydedildi!");
      toast.success("Avatar başarıyla kaydedildi!");
      return true;
    } catch (err) {
      console.error("Firebase'e kaydetme hatası:", err.message);
      toast.error("Veritabanına kaydedilemedi: " + err.message);
      return false;
    }
  };

  const handleOnAvatarExported = (event) => {
    const glbUrl = event.data.url;
    const avatarId = glbUrl.split('/').pop().replace('.glb', '');
    const renderUrl = `https://models.readyplayer.me/${avatarId}.png?scene=fullbody-posture-v1-transparent`;
    console.log("Yeni avatar oluşturuldu:", { renderUrl, glbUrl });
    setAvatarUrl(renderUrl);
    setShareUrl(glbUrl);
    setShowIframe(false);
    toast.success("Avatar oluşturuldu! Kaydetmek için 'Avatarı Kaydet' butonuna tıklayın.");
  };

  const handleCreateAvatarClick = () => {
    console.log("Yeni Avatar Oluştur butonuna basıldı!");
    setShowIframe(true);
  };

  const handleGenerateCard = () => {
    setShowShareCard(true);
    toast.success("Avatar kartı oluşturuldu!");
  };

  const handleDownload = () => {
    const shareCard = document.querySelector('.share-card');
    if (!shareCard) {
      toast.error("Paylaşım kartı bulunamadı!");
      return;
    }
    html2canvas(shareCard).then(canvas => {
      const link = document.createElement('a');
      link.download = 'my-avatar.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("Avatar kartı indirildi!");
    }).catch(error => {
      console.error("Kart indirme hatası:", error);
      toast.error("Kart indirilemedi: " + error.message);
    });
  };

  const handleShare = () => {
    const shareCard = document.querySelector('.share-card');
    if (!shareCard) {
      toast.error("Paylaşım kartı bulunamadı!");
      return;
    }
    html2canvas(shareCard).then(canvas => {
      canvas.toBlob(blob => {
        const file = new File([blob], 'my-avatar.png', { type: 'image/png' });
        if (navigator.share) {
          navigator.share({
            files: [file],
            title: 'My Avatar',
            text: 'Check out my awesome avatar created with Yelken.pro!',
          }).then(() => {
            toast.success("Avatar kartı paylaşıldı!");
          }).catch(error => {
            console.error('Paylaşım başarısız:', error);
            toast.error("Paylaşım başarısız: " + error.message);
          });
        } else {
          toast.warn('Bu cihazda paylaşım özelliği desteklenmiyor. Resmi indirebilirsiniz!');
        }
      });
    }).catch(error => {
      console.error("Kart paylaşma hatası:", error);
      toast.error("Kart paylaşılamadı: " + error.message);
    });
  };

  const handleImageError = () => {
    setErrorMessage("Avatar görüntüsü yüklenemedi. URL geçerli değil veya erişilemiyor: " + avatarUrl);
    console.error("Avatar görüntüsü yüklenemedi:", avatarUrl);
  };

  return (
    <motion.div
      className="avatar-creator-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h2>3D Avatar Oluştur</h2>

      {/* Avatar Önizleme */}
      <div className="avatar-preview-container">
        {avatarUrl ? (
          <motion.div
            className="avatar-wrapper"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src={avatarUrl}
              alt="Avatar Preview"
              className="avatar-image"
              onError={handleImageError}
            />
            <div className="avatar-platform"></div>
          </motion.div>
        ) : (
          <motion.div
            className="no-avatar-placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <p>Henüz bir avatar oluşturulmadı.</p>
          </motion.div>
        )}
        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </div>

      {/* Paylaşım Bölümü */}
      {avatarUrl && (
        <div className="share-section">
          {!showShareCard ? (
            <motion.button
              className="generate-card-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGenerateCard}
            >
              Avatar Kartını Oluştur ve Paylaş
            </motion.button>
          ) : (
            <>
              <div className="share-card">
                <img src={avatarUrl} alt="Avatar" className="share-card-image" />
                <div className="share-card-brand">
                  <span className="share-card-brand-text">Yelken</span>
                  <img src={logo} alt="Yelken Logo" className="share-card-brand-logo" />
                </div>
                <div className="share-card-site">yelken.pro</div>
                <div className="share-card-footer">
                  Bu avatar, kişisel kullanım için oluşturulmuş olup, CC BY-NC-SA 4.0 lisansı altındadır. Detaylı bilgi için <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noopener noreferrer">buraya tıklayın</a>.
                </div>
              </div>
              <div className="share-actions">
                <motion.button
                  className="download-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDownload}
                >
                  İndir
                </motion.button>
                <motion.button
                  className="share-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                >
                  Paylaş
                </motion.button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Butonlar Container */}
      <div className="button-container">
        <motion.button
          className="create-avatar-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCreateAvatarClick}
        >
          Yeni Avatar Oluştur
        </motion.button>

        {(avatarUrl || shareUrl) && (
          <motion.button
            className="submit-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSaveAvatar}
          >
            Avatarı Kaydet
          </motion.button>
        )}
      </div>

      {/* Lisans Bilgilendirme */}
      <div className="license-info">
        <h3>🛡️ Lisans Bilgilendirmesi</h3>
        <p>Bu sitede kullanılan 3D avatarlar Ready Player Me tarafından sağlanmaktadır.</p>
        <p>Oluşturulan avatarlar Creative Commons Attribution-NonCommercial-ShareAlike 4.0 International (CC BY-NC-SA 4.0) lisansı ile lisanslanmıştır.</p>
        <p>Bu lisans kapsamında:</p>
        <ul>
          <li>✅ Avatarlar kopyalanabilir, paylaşılabilir ve kişisel kullanım için düzenlenebilir.</li>
          <li>❌ Ticari amaçlarla (satış, ücretli erişim gibi) kullanılamaz.</li>
          <li>🔁 Avatarlar üzerinde yapılan değişiklikler aynı lisansla paylaşılmalıdır.</li>
          <li>📎 Kullanımda kaynak gösterimi zorunludur.</li>
        </ul>
        <p>Detaylı bilgi için <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noopener noreferrer">lisans sayfasını</a> ziyaret edin.</p>
      </div>

      {/* Iframe Modal */}
      {showIframe && (
        <div className="iframe-modal">
          <div className="iframe-modal-content">
            <button className="close-modal-button" onClick={() => setShowIframe(false)}>
              ×
            </button>
            <AvatarCreator
              subdomain="demo"
              config={config}
              style={iframeStyle}
              onAvatarExported={handleOnAvatarExported}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AvatarCreatorPage;